// WebAudio-synth sound effects + mute state. No assets — every sound is
// generated from a triangle oscillator with a quick attack/decay
// envelope. Three sounds today:
//
//   playCountInBeat   — 3 / 2 / 1 ticks during the speedrun count-in.
//   playCountInGo     — GO tick. Higher pitch, slightly louder; the
//                       "round started" cue.
//   playCommit        — region-committed chime. Two-note up-arpeggio,
//                       fires at the start of MarcEngine's celebration
//                       window so it lands with the bar going full.
//
// Mute state lives here (not in React) so engine code can check it
// synchronously without prop-drilling. localStorage backed; subscribers
// (React hooks, in practice) get notified on change.

const STORAGE_KEY = "egg-game:v1:sound-muted";

let muted = readMuted();
const muteListeners = new Set();

function readMuted() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "false") === true;
  } catch {
    return false;
  }
}

export function getMuted() {
  return muted;
}

export function setMuted(next) {
  const v = !!next;
  if (v === muted) return;
  muted = v;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
  } catch {
    // localStorage can throw in private mode / quota — fail open, the
    // in-memory flag still drives playback for the session.
  }
  for (const l of muteListeners) l(v);
}

export function subscribeMuted(listener) {
  muteListeners.add(listener);
  return () => muteListeners.delete(listener);
}

// Lazy singleton AudioContext. Browsers gate AudioContext creation on a
// user gesture; the first play call after a click (e.g., the Speedrun
// button) is inside that gesture chain, so creation succeeds. resume()
// is defensive in case the context boots suspended (some browsers do
// this even mid-gesture if the page has been backgrounded).
let ctx = null;
function getCtx() {
  if (ctx == null) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
  }
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

// Play a single triangle-wave tone with linear attack + exponential
// decay. The exponential decay reads as a more natural percussive
// envelope than linear — the tail trails off cleanly instead of
// cutting at a hard zero. attackMs is short by design (ramps from 0
// to peak in a handful of ms) so the tone reads as struck rather
// than swelled.
function playTone({
  freq,
  durationMs,
  peakGain,
  attackMs = 4,
  type = "triangle",
}) {
  if (muted) return;
  const c = getCtx();
  if (!c) return;
  const now = c.currentTime;
  const dur = durationMs / 1000;
  const attack = attackMs / 1000;

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);

  gain.gain.setValueAtTime(0, now);
  gain.gain.linearRampToValueAtTime(peakGain, now + attack);
  // exponentialRampToValueAtTime can't reach 0 — clamp to a small
  // floor and stop the oscillator at the end so it cleans up.
  gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);

  osc.connect(gain).connect(c.destination);
  osc.start(now);
  osc.stop(now + dur + 0.02);
}

// Count-in beat (3 / 2 / 1). Mid pitch, snappy decay — reads as a
// woodblock-y click, not a melodic note.
export function playCountInBeat() {
  playTone({ freq: 440, durationMs: 120, peakGain: 0.18 });
}

// Count-in GO. Higher pitch + slightly louder + slightly longer than
// the lead-in beats — "and we're off." Same waveform/envelope shape
// so it sits in the same family as 3/2/1.
export function playCountInGo() {
  playTone({ freq: 660, durationMs: 200, peakGain: 0.24 });
}

// Region-commit chime. Two triangle notes up an interval (E5 → A5),
// the second starting partway through the first so they overlap into
// a tiny arpeggio rather than reading as two separate beeps.
export function playCommit() {
  if (muted) return;
  playTone({ freq: 659.25, durationMs: 140, peakGain: 0.08 });
  // Slight delay before the second note. setTimeout is fine here —
  // we're not trying to be sample-accurate, and scheduling the second
  // tone via the AudioContext clock would complicate the early-out
  // path on mute toggle mid-chime.
  setTimeout(
    () => playTone({ freq: 880, durationMs: 180, peakGain: 0.12 }),
    70,
  );
}
